import React from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { convertSizeToString, SizeData } from '../../shared/dto/EntityRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { StyledDivComponent } from '../../shared/dto/StyleCompRepository';
import { InputWork } from '../../shared/component/headless/input/InputBody';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { useValidation } from '../../shared/hooks/useValidation';
import { DimensionsType, SelectType, SizeUnit } from '../../shared/enum/EnumRepository';
import SelectBox from '../../shared/component/SelectBox';
import { INPUT_SIZE_MIN_LENGTH, INPUT_SIZE_MAX_LENGTH } from '../../shared/constants/limits';
import toggleIcon from '../../asset/icon/toggle.png';


// 2-D  ➜ width × height
// 3-D  ➜ width × height × depth
interface SizeInputProps {
  value: SizeData;
  onChange: (next: SizeData) => void;
  StyledDiv: StyledDivComponent;
  dimensionsType: DimensionsType;
  displayValue?: string;
  onDimensionsTypeToggle?: () => void;
}


const SizeInput: React.FC<SizeInputProps> = ({
  value,
  onChange,
  StyledDiv,
  dimensionsType,
  displayValue,
  onDimensionsTypeToggle,
}) => {
  const { isEditMode } = useEditMode();
  const { checkType } = useValidation();

  const handleChange = (field: keyof SizeData, newValue: string) => {
    if (!checkType(field, newValue)) {
      return;
    };
    const updatedSize = { ...value, [field]: newValue };
    onChange(updatedSize);
  }

  const handleChangeUnit = (unit: SizeUnit) => {
    const updatedSize = { ...value, unit };
    onChange(updatedSize);
  }

  const isDimensionsVariable = dimensionsType === DimensionsType.VARIABLE;

  const convertSizeToStringWithVariable = (size: SizeData): string => {
    if (displayValue !== undefined) return displayValue;
    if (isDimensionsVariable) return "Dimensions variable";
    return convertSizeToString(size);
  }

  return (
    <>
      {isEditMode ? (
        <SizeInputContainer>
          <SizeInputComp>
            {isDimensionsVariable ? (
              <VariableComp>Dimensions variable</VariableComp>
            ) : (
              <>
                <HeadlessInput
                  value={value.height || ''}
                  handleChange={(e) => handleChange('height', e.target.value)}
                  StyledInput={NumInput}
                  placeholder={"H"}
                  minLimit={INPUT_SIZE_MIN_LENGTH}
                  maxLimit={INPUT_SIZE_MAX_LENGTH}
                />
                <Times>×</Times>
                <HeadlessInput
                  value={value.width || ''}
                  handleChange={(e) => handleChange('width', e.target.value)}
                  StyledInput={NumInput}
                  placeholder={"W"}
                  minLimit={INPUT_SIZE_MIN_LENGTH}
                  maxLimit={INPUT_SIZE_MAX_LENGTH}
                />
                <Times>×</Times>
                <HeadlessInput
                  value={value.depth || ''}
                  handleChange={(e) => handleChange('depth', e.target.value)}
                  StyledInput={NumInput}
                  placeholder={"D"}
                  minLimit={INPUT_SIZE_MIN_LENGTH}
                  maxLimit={INPUT_SIZE_MAX_LENGTH}
                />
                <SelectBoxWrapper>
                  <SelectBox
                    value={value.unit || SizeUnit.CM}
                    selectType={SelectType.SIZE_UNIT}
                    handleChange={handleChangeUnit}
                    direction={false} />
                </SelectBoxWrapper>
              </>
            )}
            {onDimensionsTypeToggle && (
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent
                backgroundImg={toggleIcon}
                onClick={onDimensionsTypeToggle}
              />
            )}
          </SizeInputComp >
        </SizeInputContainer>
      ) : (
        <StyledDiv>{convertSizeToStringWithVariable(value)}</StyledDiv>
      )}
    </>
  );
};

const SizeInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.typography.Body_04};
`;

const SizeInputComp = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NumInput = styled(InputWork)`
  min-width: 10px;
  text-align: center;
  
  text-align: center;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const Times = styled.span`
  user-select: none;
`;

const SelectBoxWrapper = styled.div`
  width: clamp(50px, 4vw, 60px);
  margin-left: clamp(4px, 2vw, 8px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const VariableComp = styled.div`
  min-width: 110px;
  width: 17ch;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default SizeInput;
